import * as _ from 'lodash';
import moment from 'moment';
import { useState } from 'react';
import type { ReactElement } from 'react';
import type { Column, SortingRule } from 'react-table';
import type { Banner } from '../../../../../typings/api';
import { useDialogContext } from '../../../../contexts/DialogContext';
import { useOnMount } from '../../../../hooks';
import { Method, RequestState } from '../../../../hooks/deprecatedAPI/makeRequest';
import useBanners, { PutOperation } from '../../../../hooks/deprecatedAPI/useBanners';
import useRequestEvents from '../../../../hooks/deprecatedAPI/useRequestEvents';
import Button from '../../../generic/Button';
import Loader from '../../../generic/Loader';
import PopperMenu from '../../../generic/PopperMenu';
import SimpleReactTable, { MINIMUM_PAGE_SIZE } from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import Header from '../../../shared/settings/SettingsPageHeader';
import ModalAddEditBanner from './ModalAddEditBanner';
import useAddEditBanner, { bodyContentsJSON } from './useAddEditBanner';

// MAX_BANNERS_COUNT is the number of active and scheduled banners that we allow per organization at any one time.
const MAX_BANNERS_COUNT = 3;

enum BannerStatus {
  Active,
  Scheduled,
  Inactive,
}

//  Determine if there are less than the max number of banners.
const lessThanMaxRows = (rows: BannerRow[]) =>
  _.sumBy(rows, row => row.status !== BannerStatus.Inactive ? 1 : 0) < MAX_BANNERS_COUNT;

type BannerRow = Pick<Banner, 'id' | 'title' | 'startDate' | 'endDate'> & {
  status: BannerStatus;
  statusName: ReactElement;
  // start is the start date displayed in a human readable format.
  start: string;
  // end is the end date displayed in a human readable format, or a dash when an end date is not defined.
  end: string;
};

const defaultSortMode: SortingRule = { id: 'status', desc: false };

const dateFormatOptions: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

const bannerStatus = (banner: Banner): Pick<BannerRow, 'status' | 'statusName'> => {
  const now = moment();
  if (banner.isDeactivated || (banner.endDate && now.isAfter(moment(banner.endDate)))) {
    return {
      status: BannerStatus.Inactive,
      statusName: (
        <span>Inactive</span>
      ),
    };
  }

  if (moment(banner.startDate).isAfter(now)) {
    return {
      status: BannerStatus.Scheduled,
      statusName: (
        <span>Scheduled</span>
      ),
    };
  }

  return {
    status: BannerStatus.Active,
    statusName: (
      <span>Active</span>
    ),
  };
};

/**
 * Settings page for notification banners.
 */
const OrganizationBanners = () => {
  const banners = useBanners();

  const [sortMode, setSortMode] = useState<SortingRule>(defaultSortMode);
  const [errorAlert, setErrorAlert] = useState<string | null>(null);

  const {
    state: addEditState,
    isEdited,
    onAddNewBanner,
    onEditBanner,
    onDuplicateBanner,
    onCloseModal,
    onTitleChange,
    onBodyChange,
    onStartDateChange,
    onEndDateChange,
    onToggleIsDismissible,
    onBannerStyleChange,
  } = useAddEditBanner();

  useRequestEvents(banners.eventEmitter, event => {
    // Add a top-level error message when a request to fetch, delete, or deactivate a banner fails.
    // All other types of requests are handled within ModalAddEditBanner.
    if (
      event.state === RequestState.Failed && (
        event.context.method === Method.GET ||
        event.context.method === Method.DELETE ||
        (event.context.method === Method.PUT && event.context.operation === PutOperation.Deactivate)
      )
    ) {
      setErrorAlert(event.error);
    } else if (event.state === RequestState.Succeeded && [Method.POST, Method.PUT].includes(event.context.method)) {
      onCloseModal();
    }
  });

  useOnMount(banners.fetchAll);

  const { onAlert, onConfirm } = useDialogContext();

  if (banners.fetchStatus.loading) {
    return (
      <>
        <Header heading="Notification Banners" isPadded>
          Display information to your colleagues while on the sign in and project index pages
        </Header>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '280px' }}>
          <Loader />
        </div>
      </>
    );
  }

  const rows: BannerRow[] = banners.data.map((banner) => ({
    id: banner.id,
    title: banner.title,
    ...bannerStatus(banner),
    startDate: banner.startDate,
    start: (new Date(banner.startDate)).toLocaleDateString(undefined, dateFormatOptions),
    // An unspecified end date sorts last: the '~' character is the last character by ASCII value.
    endDate: banner.endDate || '~',
    end: banner.endDate ? (new Date(banner.endDate)).toLocaleDateString(undefined, dateFormatOptions) : '–',
  }));

  const columns: Array<Column<BannerRow>> = [
    {
      id: 'title',
      Header: (
        <TableHeader title="Title" sort={{ id: 'title', currentMode: sortMode }} />
      ),
      accessor: 'title',
      width: 320,
      resizable: false,
    },
    {
      id: 'status',
      Header: (
        <TableHeader title="Status" sort={{ id: 'status', currentMode: sortMode }} />
      ),
      accessor: 'status',
      resizable: false,
      Cell: ({ original }: { original: BannerRow }) => original.statusName,
    },
    {
      id: 'startDate',
      Header: (
        <TableHeader title="Start" sort={{ id: 'startDate', currentMode: sortMode }} />
      ),
      accessor: 'startDate',
      resizable: false,
      Cell: ({ original }: { original: BannerRow }) => original.start,
    },
    {
      id: 'endDate',
      Header: (
        <TableHeader title="End" sort={{ id: 'endDate', currentMode: sortMode }} />
      ),
      accessor: 'endDate',
      resizable: false,
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ original }: { original: BannerRow }) => (
        <>
          <span>{original.end}</span>
          {!addEditState && banners.pendingRequestBannerIds.includes(original.id)
            ? (
              <Loader size={30} style={{ height: '30px', width: '30px' }} />
              ) : (
                <PopperMenu>
                  <PopperMenu.Item
                    onClick={() => onEditBanner(banners.data.find(({ id }) => id === original.id) as Banner)}
                >
                    View
                  </PopperMenu.Item>
                  <PopperMenu.Item
                    onClick={() => {
                      if (lessThanMaxRows(rows)) {
                        const bannerToDuplicate: Banner | undefined = banners.data.find(({ id }) => original.id === id);
                        if (bannerToDuplicate) {
                          onDuplicateBanner(bannerToDuplicate);
                        }
                      } else {
                        onAlert({
                          title: 'Banners Limit Reached',
                          body: 'You’ve reached the maximum number of active and scheduled banners allowed.',
                        });
                      }
                    }}
                >
                    Duplicate
                  </PopperMenu.Item>
                  {original.status === BannerStatus.Active && (
                  <PopperMenu.Item
                    onClick={() => onConfirm({
                      title: 'Warning',
                      body: 'Deactivating a banner will immediately stop displaying it in FOSSA. Are you sure you want to deactivate it now?',
                      onConfirm: () => banners.deactivate(original.id),
                    })}
                  >
                    Deactivate
                  </PopperMenu.Item>
                  )}
                  <PopperMenu.Item
                    onClick={() => onConfirm({
                      title: 'Warning',
                      body: 'Deleting a banner is an unrecoverable action. Are you sure you want to delete this banner?',
                      onConfirm: () => banners.remove(original.id),
                    })}
                    isWarning
                >
                    Delete
                  </PopperMenu.Item>
                </PopperMenu>
              )}
        </>
      ),
    },
  ];

  return (
    <>
      <Header heading="Notification Banners" isPadded>
        <Header.Paragraph>
          Display information to your colleagues while on the sign in and project index pages.
        </Header.Paragraph>
        {errorAlert && <Header.Errors errors={[errorAlert]} />}
      </Header>
      <header className="banner-settings__table-header">
        <span className="banner-settings__table-header__text">
          Maximum of 3 active and scheduled banners
        </span>
        <Button
          type="secondary"
          size="medium"
          onClick={() => {
            if (lessThanMaxRows(rows)) {
              onAddNewBanner();
            } else {
              onAlert({
                title: 'Banners Limit Reached',
                body: 'You’ve reached the maximum number of active and scheduled banners allowed.',
              });
            }
          }}
        >
          Add Banner
        </Button>
      </header>
      <div className="banner-table">
        <SimpleReactTable
          data={rows}
          columns={columns}
          defaultSortMode={[defaultSortMode]}
          onSortedChanged={setSortMode}
          showPagination={rows.length > MINIMUM_PAGE_SIZE}
          cacheId="banners"
        />
      </div>
      {addEditState !== null && (
        <ModalAddEditBanner
          state={addEditState}
          isEdited={isEdited}
          status={banners.createUpdateStatus}
          onCloseModal={() => {
            onCloseModal();
            // Clear all errors upon closing the modal (when the Cancel button is clicked).
            banners.clearStatusErrors();
          }}
          onTitleChange={onTitleChange}
          onBodyChange={onBodyChange}
          onStartDateChange={onStartDateChange}
          onEndDateChange={onEndDateChange}
          onToggleIsDismissible={onToggleIsDismissible}
          onBannerStyleChange={onBannerStyleChange}
          onSubmitAddEdit={() => {
            // Clear all errors before submitting.
            banners.clearStatusErrors();
            if (addEditState?.mode === 'add') {
              // no await
              banners.create({
                ...addEditState.banner,
                body: bodyContentsJSON(addEditState.banner),
              });
            } else if (addEditState?.mode === 'edit') {
              // no await
              banners.update(addEditState.banner.id, {
                ...addEditState.banner,
                body: bodyContentsJSON(addEditState.banner),
              });
            }
          }}
        />
      )}
    </>
  );
};

export default OrganizationBanners;
